import lodashIsNaN from 'lodash/fp/isNaN';
import isNil from 'lodash/fp/isNil';
import round from 'lodash/fp/round';
import type { Waypoint } from '../store/facade/facadeApiV2';
import { TourEventType, TourStatus, type EventCreated, type TourResponse } from '../store/facade/facadeApiV3';

export const MMT_GUIDANCE_THRESHOLD_MS = 10 * 60 * 1000;

export const isSrpOriginatedTour = (tour: TourResponse): boolean => {
    const createdEvent = tour.events?.timelineEvents?.find(
        (e): e is EventCreated => e.tourEventType === TourEventType.Created
    );
    return createdEvent?.requestedByVehicle === false;
};

export const isDepartureExpired = (desiredDepartureTime?: string): boolean => {
    if (!desiredDepartureTime) {
        return false;
    }
    const departure = new Date(desiredDepartureTime).getTime();
    if (lodashIsNaN(departure)) {
        return false;
    }
    return departure + MMT_GUIDANCE_THRESHOLD_MS < Date.now();
};

export const shouldBlockSyncForExpiredDeparture = (tour: TourResponse): boolean => {
    const eligibleStatuses: TourStatus[] = [TourStatus.Draft, TourStatus.Synchronized];
    if (!eligibleStatuses.includes(tour.status)) {
        return false;
    }
    if (!isSrpOriginatedTour(tour)) {
        return false;
    }
    return isDepartureExpired(tour.departure.desiredDepartureTime);
};

export const getCargoAtStopovers = (waypoints: Waypoint[], initialCargoWeight: number): number[] => {
    const result = [initialCargoWeight];

    for (const waypoint of waypoints) {
        if (waypoint.details && 'weightDelta' in waypoint.details) {
            const lastValue = result.at(-1);
            if (lastValue !== undefined) {
                result.push(lastValue - waypoint.details.weightDelta);
            }
        }
    }

    return result.slice(1);
};

export const getBatteryLevelPercentage = (initBattery?: number, batteryLevel?: number) => {
    if (isNil(batteryLevel) || isNil(initBattery)) {
        return undefined;
    }
    const batteryPercentage = (batteryLevel * 100) / initBattery;
    return Math.max(0, round(batteryPercentage));
};

export const isUUID = (value: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    return uuidRegex.test(value);
};
